/**
 * §A.2 · Las cinco métricas de plataforma.
 *
 * ## Se llama `tiposDeMetricas.ts` y no `metricas.ts`
 *
 * Por lo mismo que `tiposDeExamen.ts` y `tiposDelTemario.ts`: en macOS el sistema de
 * ficheros no distingue mayúsculas, así que `metricas.ts` y `Metricas.tsx` son el
 * mismo nombre para resolver un `import`. `tsc` acertaba —prueba `.ts` antes que
 * `.tsx`— y el servicio de proyecto de ESLint no: «Metricas.tsx was not found by the
 * project service», con la pantalla dentro de `include`.
 *
 * Es la tercera vez que pasa y la primera que no costó buscarla, porque estaba
 * escrita. En Linux —la CI— no habría pasado, que es la peor forma de que pase.
 *
 * ## Un mes sin pasada responde `404`, y eso no es un fallo
 *
 * Las cinco se sirven **precalculadas** por `platform.metrics_rollup`, que corre a
 * las 02:30: con doscientos clubes y cuatro años de asistencia, el MRR en vivo es una
 * consulta que tarda segundos y que se pide veinte veces al día.
 *
 * La consecuencia manda en toda la pantalla. Un mes que el proceso no ha recorrido
 * responde `404` **y no un cero**, y el contrato dice por qué con estas palabras: «un
 * cero es una cifra, y una cifra falsa de MRR es peor que un hueco, porque se copia a
 * un correo y ya nadie la comprueba».
 *
 * Así que aquí `404` es un dato y se pinta como tal —«este mes todavía no se ha
 * calculado»—, y solo lo demás es error. Es la misma familia del cero inventado de
 * §P.9, con el servidor negándose de antemano.
 *
 * ## Dos cifras que el esquema no sostiene, y salen en blanco
 *
 * `cac_cents` es **siempre `null`**: no hay ninguna tabla de gasto de marketing, y
 * «un CAC inventado acaba en un correo a inversores». Y los extras miden **volumen
 * que pasa por la plataforma, no ingreso nuestro**, porque no hay tasa de comisión
 * configurada en ningún sitio.
 *
 * Las dos se dicen en pantalla. Una métrica que parece medir algo que no mide es peor
 * que una métrica que falta.
 */

/** Las cinco llevan cabecera: el mes y **cuándo se calculó**. */
interface Cabecera {
  readonly period: string;
  readonly computed_at: Date | string;
}

export interface Mrr extends Cabecera {
  readonly mrr_cents: number;
  readonly paying_clubs: number;
  /** Aparte del MRR a propósito: un club en prueba no es ingreso. */
  readonly trialing_clubs: number;
  readonly new_cents: number;
  readonly expansion_cents: number;
  readonly contraction_cents: number;
  readonly churned_cents: number;
  readonly by_plan: readonly {
    readonly plan_code: string;
    readonly clubs: number;
    readonly mrr_cents: number;
  }[];
}

export interface Churn extends Cabecera {
  readonly cohorts: readonly {
    /** El mes del alta, `YYYY-MM`. */
    readonly cohort: string;
    readonly size: number;
    readonly churned: number;
    readonly alive: number;
    readonly churn_rate: number;
  }[];
}

export interface Alumnos extends Cabecera {
  readonly students: number;
  readonly academies_with_students: number;
  readonly avg_per_academy: number;
}

export interface Adquisicion extends Cabecera {
  readonly channels: readonly {
    readonly source: string;
    readonly leads: number;
    readonly won: number;
    readonly lost: number;
    readonly open: number;
    readonly conversion_rate: number;
    /** Siempre `null`. Ver la cabecera. */
    readonly cac_cents: number | null;
  }[];
}

export interface Extras extends Cabecera {
  readonly lines: readonly {
    readonly kind: string;
    readonly volume_cents: number;
    readonly count: number;
    readonly academies: number;
  }[];
  readonly total_volume_cents: number;
}

/** `2026-09`. El mes en curso, que es el que se mira al entrar. */
export const mesEnCurso = (hoy = new Date()): string =>
  `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;

/**
 * `2026-09` → «septiembre de 2026».
 *
 * Con `Intl` y **sin `capitalize`**: ya convirtió «septiembre de 2026» en «Septiembre
 * De 2026» una vez. La mayúscula, si hace falta, la pone `::first-letter` en CSS.
 *
 * El día 1 a mediodía y no a medianoche: construir `2026-09-01T00:00:00Z` y
 * formatearlo en local saca **agosto** en cualquier zona al oeste de Greenwich.
 */
const MES_LARGO = new Intl.DateTimeFormat('es-ES', {
  month: 'long',
  year: 'numeric',
});

export function mesEnPalabras(period: string): string {
  const [anio, mes] = period.split('-').map(Number);
  if (!anio || !mes) return period;
  return MES_LARGO.format(new Date(anio, mes - 1, 1, 12));
}

/**
 * El mes anterior a uno dado, para el selector.
 *
 * `new Date(anio, mes - 2, 1)` con `mes = 1` da diciembre del año anterior sin
 * ninguna condición: el constructor normaliza los meses fuera de rango, y eso es
 * mejor que un `if` que alguien copiará mal.
 */
export function mesAnterior(period: string): string {
  const [anio, mes] = period.split('-').map(Number);
  if (!anio || !mes) return period;
  const d = new Date(anio, mes - 2, 1, 12);
  return mesEnCurso(d);
}

export function mesSiguiente(period: string): string {
  const [anio, mes] = period.split('-').map(Number);
  if (!anio || !mes) return period;
  return mesEnCurso(new Date(anio, mes, 1, 12));
}

/**
 * El movimiento del MRR, en las cuatro piezas que piden decisiones distintas.
 *
 * «"Subió 300 €" no dice si entraron ocho clubes o si tres subieron de plan y dos se
 * fueron, y esas dos historias piden decisiones opuestas.» Por eso las cuatro salen
 * juntas y el neto se calcula aquí y no se pide: es una resta, y pedir una resta al
 * servidor es una cifra más que puede discrepar.
 */
export const movimientoNeto = (m: Mrr): number =>
  m.new_cents + m.expansion_cents - m.contraction_cents - m.churned_cents;

/**
 * El nombre de un canal de adquisición, en algo legible.
 *
 * `crm_leads.source` es texto libre —`landing`, `seo`, `boca_a_boca`— y no hay tabla
 * de nombres bonitos. Misma regla que `nombreDeInterruptor`: se arregla la forma, no
 * se inventa el contenido.
 */
export function nombreDeCanal(source: string): string {
  const limpio = source.replace(/[._-]/g, ' ').trim();
  return limpio === '' ? '—' : limpio[0]!.toUpperCase() + limpio.slice(1);
}

/**
 * Los conceptos de los extras.
 *
 * **Copiados de la migración, no recordados.** La primera versión de esta tabla decía
 * `material_delivered` y `competition_entry`, que suenan bien y no existen: los
 * valores que escribe `platform_metrics_rollup` en `dimension` son `material` y
 * `competition`, y se leen en
 * `db/migrations/20260910000300_metricas_de_plataforma.sql`.
 *
 * Es la quinta tabla de enum inventada de memoria en este proyecto, y van cinco
 * veces que la regla la caza: **un enum se copia del esquema o del código**. Lo que
 * la hace peligrosa es que no falla — cae al `?? kind` y la pantalla enseña
 * «material» en minúscula, que parece una decisión de estilo. 18-09-2026.
 */
export const etiquetaDeExtra = (kind: string): string =>
  ({
    material: 'Material entregado',
    competition: 'Inscripciones a competición',
  })[kind] ?? kind;

/**
 * Una cifra de movimiento con su signo, y **sin signo cuando es cero**.
 *
 * «−0,00 €» y «+0,00 €» es lo que salía en un mes sin bajas ni altas, porque el signo
 * estaba escrito a mano delante del importe. Un cero no tiene dirección, y un menos
 * pegado a un cero se lee como un error del programa.
 *
 * `negativa` dice si esta cifra **resta** en el movimiento: las bajadas de plan y las
 * bajas se enseñan en negativo aunque el servidor las manda en positivo, que es lo
 * que permite sumar la columna y que cuadre con el neto.
 */
export function conSigno(
  centimos: number,
  euros: (c: number) => string,
  negativa = false
): string {
  if (centimos === 0) return euros(0);
  const signo = negativa ? '−' : centimos < 0 ? '−' : '+';
  return `${signo}${euros(Math.abs(centimos))}`;
}
